import React, { useEffect, useRef } from 'react';
import { MapPin, Phone } from 'lucide-react';
import { motion } from 'framer-motion';
import L from 'leaflet';
const SERVICE_AREAS = [{
  name: 'San Diego',
  lat: 32.7157,
  lng: -117.1611
}, {
  name: 'El Cajon',
  lat: 32.7948,
  lng: -116.9625
}, {
  name: 'La Mesa',
  lat: 32.7678,
  lng: -117.0231
}, {
  name: 'Lakeside',
  lat: 32.8578,
  lng: -116.9222
}, {
  name: 'Santee',
  lat: 32.8384,
  lng: -116.9739
}, {
  name: 'Alpine',
  lat: 32.8356,
  lng: -116.7664
}, {
  name: 'Los Coches Rd',
  lat: 32.82,
  lng: -116.91
}, {
  name: 'Rancho San Diego',
  lat: 32.7472,
  lng: -116.9311
}, {
  name: 'Chula Vista',
  lat: 32.6401,
  lng: -117.0842
}, {
  name: 'Clairemont Mesa Blvd',
  lat: 32.84,
  lng: -117.2
}];
function ServiceAreaMap() {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;
    const map = L.map(mapRef.current, {
      center: [32.78, -117.0],
      zoom: 10,
      scrollWheelZoom: false,
      zoomControl: true
    });
    mapInstanceRef.current = map;
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors'
    }).addTo(map);
    const markerIcon = L.divIcon({
      className: 'custom-marker',
      html: `<div style="
        background-color: #2D6A4F;
        width: 28px;
        height: 28px;
        border-radius: 50%;
        border: 3px solid #ffffff;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
      "><div style="
        width: 8px;
        height: 8px;
        background: #52B788;
        border-radius: 50%;
      "></div></div>`,
      iconSize: [28, 28],
      iconAnchor: [14, 14],
      popupAnchor: [0, -16]
    });
    SERVICE_AREAS.forEach(area => {
      L.marker([area.lat, area.lng], {
        icon: markerIcon
      }).addTo(map).bindPopup(`<div style="font-family: Inter, sans-serif; font-weight: 600; font-size: 14px; color: #1A1A1A; padding: 4px 2px;">${area.name}</div>`);
    });
    const bounds = L.latLngBounds(SERVICE_AREAS.map(a => [a.lat, a.lng]));
    map.fitBounds(bounds, {
      padding: [40, 40]
    });
    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
  }, []);
  return <div ref={mapRef} className="w-full h-full min-h-[320px] lg:min-h-[380px] rounded-2xl overflow-hidden z-0" data-id="element-173" />;
}
export function ServiceAreas() {
  return <section id="areas" className="py-14 bg-white" data-id="element-174">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-175">
        <div className="text-center mb-8" data-id="element-176">
          <span className="text-primary font-bold tracking-wider text-sm uppercase mb-2 block" data-id="element-177">
            WHERE WE WORK
          </span>
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-darkCharcoal mb-3" data-id="element-178">
            Proudly Serving San Diego County
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-179">
            We're local — and we're close to you. YEI Mateo Lawn Care serves
            homeowners across the entire San Diego County area.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 items-start" data-id="element-180">
          <motion.div initial={{
          opacity: 0,
          scale: 0.97
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="lg:col-span-3 rounded-2xl overflow-hidden shadow-lg border border-gray-200" data-id="element-181">
            <ServiceAreaMap data-id="element-182" />
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="lg:col-span-2" data-id="element-183">
            <h3 className="text-lg font-bold text-darkCharcoal mb-4" data-id="element-184">
              Areas We Serve
            </h3>

            <div className="grid grid-cols-2 gap-2 mb-6" data-id="element-185">
              {SERVICE_AREAS.map((area, index) => <div key={index} className="flex items-center gap-2 bg-lightGray rounded-lg px-3 py-2.5 border border-gray-100" data-id="element-186">
                  <MapPin size={14} className="text-primary shrink-0" data-id="element-187" />
                  <span className="font-medium text-darkCharcoal text-sm" data-id="element-188">
                    {area.name}
                  </span>
                </div>)}
            </div>

            <div className="bg-primary/5 border border-primary/20 rounded-xl p-4" data-id="element-189">
              <p className="text-darkCharcoal font-medium text-sm mb-2" data-id="element-190">
                Don't see your city? Call us — we may still serve your area.
              </p>
              <a href="tel:6192492966" className="inline-flex items-center gap-2 text-primary font-bold hover:text-primary/80 transition-colors" data-id="element-191">
                <Phone size={18} data-id="element-192" />
                (619) 249-2966
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}